import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { 
  HomeIcon, 
  MapPinIcon, 
  BanknotesIcon,
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  PhoneIcon,
  HeartIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

interface Property {
  id: number;
  title: string;
  location: string;
  price: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  image: string;
  type: string;
  featured: boolean;
}

const defaultProperties: Property[] = [
  {
    id: 1,
    title: 'Deniz manzaralı villa',
    location: 'Bebek, İstanbul',
    price: '₺45.000.000',
    bedrooms: 5,
    bathrooms: 4,
    area: 450,
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80',
    type: 'Villa',
    featured: true
  },
  {
    id: 2,
    title: 'Şehir merkezinde lüks daire',
    location: 'Nişantaşı, İstanbul',
    price: '₺18.500.000',
    bedrooms: 3,
    bathrooms: 2,
    area: 180,
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80',
    type: 'Daire',
    featured: true
  },
  {
    id: 3,
    title: 'Özel Aile Evi',
    location: 'Etiler, İstanbul',
    price: '₺28.000.000',
    bedrooms: 4,
    bathrooms: 3,
    area: 320,
    image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&q=80',
    type: 'Müstakil Ev',
    featured: false
  },
  {
    id: 4,
    title: 'Lüks Çatıkatı',
    location: 'Levent, İstanbul',
    price: '₺32.000.000',
    bedrooms: 4,
    bathrooms: 3,
    area: 280,
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80',
    type: 'Penthouse',
    featured: true
  },
  {
    id: 5,
    title: 'Lüks Dublex Daire',
    location: 'Kadıköy, İstanbul',
    price: '₺12.500.000',
    bedrooms: 3,
    bathrooms: 2,
    area: 200,
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
    type: 'Dublex',
    featured: false
  },
  {
    id: 6,
    title: 'sahilyolu Villa',
    location: 'Bodrum, Muğla',
    price: '₺55.000.000',
    bedrooms: 6,
    bathrooms: 5,
    area: 550,
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
    type: 'Villa',
    featured: true
  },
  {
    id: 7,
    title: 'Modern Stüdyo Daire',
    location: 'Şişli, İstanbul',
    price: '₺6.500.000',
    bedrooms: 1,
    bathrooms: 1,
    area: 65,
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80',
    type: 'Stüdyo',
    featured: false
  },
  {
    id: 8,
    title: 'Havuzlu Bahçeli Villa',
    location: 'Çeşme, İzmir',
    price: '₺38.000.000',
    bedrooms: 5,
    bathrooms: 4,
    area: 420,
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
    type: 'Villa',
    featured: false
  },
  {
    id: 9,
    title: 'Beyoğlu apartmanı',
    location: 'Beyoğlu, İstanbul',
    price: '₺15.000.000',
    bedrooms: 3,
    bathrooms: 2,
    area: 150,
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80',
    type: 'Daire',
    featured: false
  }
];

const Home = () => {
  const [properties, setProperties] = useState<Property[]>(defaultProperties);
  const [favorites, setFavorites] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('Tümü');
  const [showJson, setShowJson] = useState(false);

  // Send to webhook whenever properties change
  useEffect(() => {
    const sendToWebhook = async () => {
      try {
        await fetch('https://n8n.canhalisturk.com/webhook-test/emlakasistanasset', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(properties)
        });
        console.log('Properties sent to webhook');
      } catch (error) {
        console.error('Error sending to webhook:', error);
      }
    };

    if (properties.length > 0) {
      sendToWebhook();
    }
  }, [properties]);

  // Copy JSON to clipboard
  const copyJsonToClipboard = () => {
    const json = JSON.stringify(properties, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      toast.success('JSON kopyalandı!');
    }).catch(() => {
      toast.error('Kopyalama başarısız');
    });
  };

  // Download JSON file
  const downloadJson = () => {
    const json = JSON.stringify(properties, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'emlak-listesi.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('JSON indirildi!');
  };

  // Delete property
  const deleteProperty = (id: number) => {
    if (confirm('Bu ilanı silmek istediğinizden emin misiniz?')) {
      const updated = properties.filter(p => p.id !== id);
      setProperties(updated);
      toast.success('İlan silindi!');
    }
  };

  // Add quick property
  const addQuickProperty = () => {
    const newProperty: Property = {
      id: Date.now(),
      title: 'Yeni İlan',
      location: 'İstanbul',
      price: '₺10.000.000',
      bedrooms: 3,
      bathrooms: 2,
      area: 150,
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80',
      type: 'Daire',
      featured: false
    };
    setProperties([...properties, newProperty]);
    toast.success('Yeni ilan eklendi!');
  };

  const toggleFavorite = (id: number) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(fav => fav !== id) : [...prev, id]
    );
  };

  const filteredProperties = properties.filter((property: Property) => {
    const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'Tümü' || property.type === selectedType;
    return matchesSearch && matchesType;
  });

  const propertyTypes = ['Tümü', 'Villa', 'Daire', 'Penthouse', 'Dublex', 'Müstakil Ev', 'Stüdyo'];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-6">
                <PhoneIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" />
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  Sesli Asistan ile 7/24 Hizmetinizdeyiz
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                Hayalinizdeki Evi
                <br />
                <span className="text-primary-600 dark:text-primary-400">Sesli Asistan</span> ile Bulun
              </h1>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                Yapay zeka destekli sesli asistanımız ile emlak arama deneyiminizi kolaylaştırın. 
                Sağ alttaki butona tıklayarak hemen görüşme başlatın!
              </p>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Konum, emlak tipi veya özellik ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <BuildingOffice2Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Emlak Tipi:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {propertyTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedType === type
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Öne Çıkan İlanlar
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {filteredProperties.length} emlak bulundu
              </p>
            </div>
            <button
              onClick={addQuickProperty}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              <PlusIcon className="w-5 h-5" />
              Hızlı İlan Ekle
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((property: Property, index: number) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="group relative bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={property.image}
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {property.featured && (
                    <div className="absolute top-4 left-4 px-3 py-1 bg-primary-600 text-white text-xs font-semibold rounded-full">
                      ÖNE ÇIKAN
                    </div>
                  )}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button
                      onClick={() => deleteProperty(property.id)}
                      className="p-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full hover:scale-110 transition-all shadow-lg"
                      title="İlanı Sil"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => toggleFavorite(property.id)}
                      className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full hover:scale-110 transition-transform"
                    >
                      {favorites.includes(property.id) ? (
                        <HeartSolidIcon className="h-5 w-5 text-red-500" />
                      ) : (
                        <HeartIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                  <div className="absolute bottom-4 left-4 px-3 py-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{property.type}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {property.title}
                  </h3>
                  <div className="flex items-center text-gray-600 dark:text-gray-400 mb-4">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{property.location}</span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center">
                        <HomeIcon className="h-4 w-4 mr-1" />
                        <span>{property.bedrooms} Oda</span>
                      </div>
                      <div className="flex items-center">
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span>{property.bathrooms} Banyo</span>
                      </div>
                      <div className="flex items-center">
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span>{property.area} m²</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center text-2xl font-bold text-primary-600 dark:text-primary-400">
                        <BanknotesIcon className="h-6 w-6 mr-1" />
                        {property.price}
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">
                      Detaylar
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <PhoneIcon className="h-16 w-16 mx-auto mb-6 opacity-90" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Sesli Asistan ile Hemen İletişime Geçin
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto mb-8">
              Yapay zeka destekli sesli asistanımız 7/24 hizmetinizde. Sağ alttaki butona tıklayarak 
              emlak ihtiyaçlarınız hakkında konuşmaya başlayın!
            </p>
            <div className="inline-flex items-center px-6 py-3 bg-white text-primary-700 rounded-lg font-semibold animate-pulse">
              <PhoneIcon className="h-5 w-5 mr-2" />
              Sağ Alttaki Butona Tıklayın
            </div>
          </motion.div>
        </div>
      </section>

      {/* JSON Data Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900" style={{ display: 'none' }} >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Emlak Verileri (JSON)
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Sesli asistan eğitimi için kullanılacak veriler
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={copyJsonToClipboard}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors shadow-md"
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />
                  Kopyala
                </button>
                <button
                  onClick={downloadJson}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-md"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  İndir
                </button>
                <button
                  onClick={() => setShowJson(!showJson)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg transition-colors"
                >
                  {showJson ? 'Gizle' : 'Göster'}
                </button>
              </div>
            </div>

            {showJson && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-gray-900 rounded-xl p-6 overflow-auto max-h-[600px]"
              >
                <pre className="text-sm text-green-400 font-mono">
                  {JSON.stringify(properties, null, 2)}
                </pre>
              </motion.div>
            )}

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  ℹ
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Otomatik Webhook Entegrasyonu
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Bu veriler otomatik olarak <code className="px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded">
                      https://n8n.canhalisturk.com/webhook-test/emlakasistanasset
                    </code> adresine gönderilmektedir.
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                    📊 Toplam {properties.length} ilan • 🌟 {properties.filter(p => p.featured).length} öne çıkan
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
